import {
  Autocomplete,
  Box,
  Button,
  Grid,
  ListItemText,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import { addEditIssueReasonSchema } from "../../schemas";
import useFetchIssueReasonById from "../../hooks/useFetchReasonById";
import useFetchAllClassifications from "../../hooks/useFetchAllClassifications";
import {
  addIssueReason,
  updateIssueReason,
} from "../../services/reasonService";
import Loader from "../../components/Molecules/Loader/Loader";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import { IIssueReason, IIssueReasonPayload } from "../../types/Reason";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useToast } from "../../services/utils";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useAuth } from "../../contexts/AuthContext";

const AddEditReason: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAdmin } = useAuth();
  const { id: reasonId } = useParams();
  const isEditMode = Boolean(reasonId);

  const [formData, setFormData] = useState<IIssueReason>({
    reason_name: "",
    issue_classification_id: undefined,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] =
    useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);

  const [reasonData] = useFetchIssueReasonById({
    setIsLoading,
    currentReasonId: reasonId,
  });

  const [classificationsData] = useFetchAllClassifications({
    setIsLoading: undefined,
  });

  const activeClassifications = (classificationsData || []).filter(
    (c: any) => c.is_active
  );

  const buildPayload = () => {
    return {
      issue_reason: {
        issue_classification_id: formData.issue_classification_id,
        reason_name: formData.reason_name,
        is_active: true,
      },
    };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/issue-reason/${reasonId}`
          : "/issue-reason",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: reasonId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("Payload copied to clipboard!", "success");
  };

  const handleFormData = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleClassificationChange = (_event: any, newValue: any) => {
    setFormData({
      ...formData,
      issue_classification_id: newValue?.id || undefined,
    });
  };

  const validateField = async (name: string, value: any) => {
    try {
      await addEditIssueReasonSchema.validateAt(name, { [name]: value });
      setErrors((prevErrors: any) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const onSaveReason = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await addEditIssueReasonSchema.validate(formData, { abortEarly: false });

      // Build the payload with the required structure - always send is_active: true
      const reqBody: IIssueReasonPayload = {
        issue_reason: {
          issue_classification_id: formData.issue_classification_id as number,
          reason_name: formData.reason_name,
          is_active: true,
        },
      };

      let response: any;
      if (reasonId) {
        response = await updateIssueReason({
          currentReasonId: reasonId,
          payload: reqBody,
        });
      } else {
        response = await addIssueReason({ payload: reqBody });
      }
      if (response) {
        showToast(
          `Reason ${reasonId ? "updated" : "created"} successfully!`,
          "success"
        );
        navigate("/reason");
      }
    } catch (validationErrors: any) {
      if (validationErrors.inner) {
        const newErrors: { [key: string]: string } = {};
        validationErrors.inner.forEach(
          (error: { path: string | number; message: string }) => {
            newErrors[error.path] = error.message;
          }
        );
        setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
      } else {
        console.error(
          `Cannot ${reasonId ? "update" : "create"} reason:`,
          validationErrors
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (reasonData && reasonData?.id) {
      setFormData({
        reason_name: reasonData.reason_name || "",
        issue_classification_id: reasonData.issue_classification_id,
      });
      setInitialFormData(
        JSON.parse(
          JSON.stringify({
            reason_name: reasonData.reason_name || "",
            issue_classification_id: reasonData.issue_classification_id,
          })
        )
      );
    } else if (!reasonId) {
      setInitialFormData({
        reason_name: "",
        issue_classification_id: undefined,
      });
    }
  }, [reasonData, reasonId]);

  useEffect(() => {
    if (reasonId && reasonData?.reason_name) {
      setCurrentBreadcrumbPage({
        name: `Edit ${reasonData.reason_name}`,
        id: reasonData.id,
      });
    } else {
      setCurrentBreadcrumbPage({
        name: reasonId ? "Edit Reason" : "Add Reason",
        id: reasonId || null,
      });
    }
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [reasonId, reasonData, setCurrentBreadcrumbPage]);

  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      setHasChanges(
        JSON.stringify(formData) !== JSON.stringify(initialFormData)
      );
    } else {
      setHasChanges(false);
    }
  }, [formData, initialFormData]);

  const selectedClassification =
    activeClassifications.find(
      (c: any) => c.id === formData.issue_classification_id
    ) || null;

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <form onSubmit={onSaveReason} autoComplete="off">
        <Box className="text-box-card compact-text-box-card">
          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Classification
                <span className="required-asterisk">*</span>
              </label>
              <Autocomplete
                options={activeClassifications}
                getOptionLabel={(option: any) =>
                  option?.issue_classification_name || ""
                }
                value={selectedClassification}
                onChange={handleClassificationChange}
                onBlur={() =>
                  validateField(
                    "issue_classification_id",
                    formData.issue_classification_id
                  )
                }
                renderInput={(params) => (
                  <TextField
                    name="issue_classification_id"
                    style={{ color: "#000000" }}
                    {...params}
                    placeholder="Select Classification"
                    className={`form-control-autocomplete ${
                      errors?.issue_classification_id ? "has-error" : ""
                    }`}
                    error={!!errors?.issue_classification_id}
                    helperText={errors?.issue_classification_id || ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Reason Name
                <span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="reason_name"
                onChange={handleFormData}
                className={`form-control ${
                  errors?.reason_name ? "has-error" : ""
                }`}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                error={!!errors?.reason_name}
                helperText={errors?.reason_name || ""}
                value={formData?.reason_name}
                placeholder="Enter reason name"
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <FlexBetween
            gap="3rem"
            sx={{ marginTop: 4, display: "flex", justifyContent: "flex-end" }}
          >
            <ViewJsonSplitButton
              onViewJson={() => {
                setValidationResponse(null);
                setIsPayloadDialogOpen(true);
              }}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={isEditMode ? !hasChanges : false}
            >
              <SaveOutlinedIcon /> &nbsp; {isEditMode ? "Update" : "Save"}
            </Button>
          </FlexBetween>
        </Box>
      </form>

      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Reason JSON"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </Box>
  );
};

export default AddEditReason;
